import * as React from 'react';
import { getDefaultLocale } from './seachpage-helpers';

// Decimal/group separators for a culture (nl-NL -> {group:'.', decimal:','}, en-US -> {group:',', decimal:'.'}).
function separators(locale: string): { group: string; decimal: string } {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(11111.1);
    return {
      group: parts.find(p => p.type === 'group')?.value ?? ',',
      decimal: parts.find(p => p.type === 'decimal')?.value ?? '.',
    };
  } catch {
    return { group: ',', decimal: '.' };
  }
}

// Invariant numeric string ("1234.56") -> localized display ("1.234,56").
function formatDisplay(invariant: string, locale: string, decimals?: number): string {
  if (invariant == null || invariant === '') return '';
  const num = Number(invariant);
  if (isNaN(num)) return invariant;
  const opts: Intl.NumberFormatOptions =
    decimals != null
      ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
      : { maximumFractionDigits: 20 };
  return num.toLocaleString(locale, opts);
}

// Localized / typed input -> invariant numeric string ("1234.56").
// Both '.' and ',' are always treated as the decimal separator (so "200.41" and "200,41" are equal).
// If several separators are present (e.g. a pasted grouped value), the LAST one is the decimal point
// and any earlier separators are dropped as grouping.
function toInvariant(input: string): string {
  let s = (input ?? '').trim().replace(/\s/g, '');
  if (s === '') return '';
  s = s.replace(/,/g, '.');
  const i = s.lastIndexOf('.');
  if (i !== -1) s = s.slice(0, i).replace(/\./g, '') + s.slice(i);
  return s;
}

interface AmountInputProps {
  /** Invariant value (dot decimal, no grouping), e.g. "1234.56". May be empty. */
  value: string;
  /** Emits the invariant value (dot decimal, no grouping) for the API. */
  onChange: (invariant: string) => void;
  className?: string;
  placeholder?: string;
  /** Fixed fraction digits for display (amounts = 2). Undefined preserves precision. */
  decimals?: number;
}

/**
 * Number/amount input that displays in the configured culture (getDefaultLocale()) while keeping the
 * canonical value invariant (dot decimal, no grouping) toward the API. The value is shown grouped/
 * formatted when not focused, and as a plain (ungrouped) localized number while editing.
 */
export function AmountInput({ value, onChange, className, placeholder, decimals }: AmountInputProps) {
  const locale = getDefaultLocale();
  const sep = React.useMemo(() => separators(locale), [locale]);
  const [focused, setFocused] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  const display = focused ? draft : formatDisplay(value, locale, decimals);

  function handleFocus() {
    // Edit from a localized, ungrouped form (just the decimal separator).
    const num = Number(value);
    setDraft(value === '' || isNaN(num) ? (value ?? '') : String(value).replace('.', sep.decimal));
    setFocused(true);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const text = e.target.value;
    setDraft(text);
    onChange(toInvariant(text));
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      placeholder={placeholder}
      value={display}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={() => setFocused(false)}
    />
  );
}
